import React, { useEffect, useState } from "react";
import { Typography, IconButton, Button } from "@material-tailwind/react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchOrderRfq,
    fetchPurchaseOrderLogs,
} from "@/store/slices/orderSlice";
import DownloadFile from "@/components/DownloadFile";
import { useTranslation } from "react-i18next";

function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return (
        d.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
        }) +
        " | " +
        d.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        })
    );
}

// Presentational list of payment revisions styled like the provided image
export function OrderRfq({ rfq_id }) {
    const dispatch = useDispatch();
    const { t } = useTranslation("order");
    const [filter, setFilter] = useState("");

    const { order_rfq, loading, purchase_order_logs } = useSelector(
        (state) => state.order
    );

    useEffect(() => {
        if (rfq_id)
            dispatch(fetchOrderRfq({ rfq_id: rfq_id, filter: filter }));
    }, [rfq_id, dispatch, filter]);

    // Fetch customer purchase order logs separately
    useEffect(() => {
        if (rfq_id) {
            dispatch(fetchPurchaseOrderLogs({ rfq_id }));
        }
    }, [rfq_id, dispatch]);

    return (
        <div className="w-full">
            {/* Controls Bar - Order Items (adapted for Payments) */}
            <div className="w-full flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                <div className="flex items-center gap-4">
                    <Typography
                        variant="h6"
                        color="blue-gray"
                        className="font-bold"
                    >
                        {t("rfqs_title", { count: order_rfq?.length ?? 0 })}
                    </Typography>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-gray-200">
                            <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                {t("revision_header")}
                            </th>
                            <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                {t("amount_header")}
                            </th>
                            <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                {t("status_header")}
                            </th>
                            <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                {t("date_header")}
                            </th>
                            <th className="py-3 px-4 text-xs text-xs font-semibold uppercase tracking-wider text-gray-500">
                                {t("action_header")}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {!order_rfq?.length ? (
                            <tr>
                                <td
                                    colSpan="5"
                                    className="py-10 text-center text-gray-500"
                                >
                                    {t("no_rfqs_message")}
                                </td>
                            </tr>
                        ) : (
                            order_rfq.map((item, index) => (
                                <tr
                                    key={item.id ?? index}
                                    className="hover:bg-gray-50 transition-colors"
                                >
                                    {/* Revision Number */}
                                    <td className="py-4 px-4">
                                        <Typography className="text-sm font-medium text-gray-900">
                                            #{order_rfq.length - index}
                                        </Typography>
                                    </td>

                                    {/* Amount */}
                                    <td className="py-4 px-4">
                                        <Typography className="text-sm font-semibold text-gray-900">
                                            <span className="text-xs font-normal text-gray-500 mr-1">
                                                {item?.currency_code ?? ""}
                                            </span>
                                            {typeof item.amount === "number"
                                                ? item.amount.toLocaleString(
                                                    undefined,
                                                    {
                                                        minimumFractionDigits: 2,
                                                    }
                                                )
                                                : item.amount}
                                        </Typography>
                                    </td>

                                    {/* Status Badge */}
                                    <td className="py-4 px-4">
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                            ${item.status === "pending"
                                                    ? "bg-yellow-100 text-yellow-800"
                                                    : ""
                                                }
                                            ${item.status === "approved"
                                                    ? "bg-green-100 text-green-800"
                                                    : ""
                                                }
                                            ${item.status === "rejected"
                                                    ? "bg-red-100 text-red-800"
                                                    : ""
                                                }
                                        `}
                                        >
                                            <span
                                                className={`mr-1.5 h-1.5 w-1.5 rounded-full 
                                                ${item.status === "pending"
                                                        ? "bg-yellow-400"
                                                        : ""
                                                    }
                                                ${item.status === "approved"
                                                        ? "bg-green-400"
                                                        : ""
                                                    }
                                                ${item.status === "rejected"
                                                        ? "bg-red-400"
                                                        : ""
                                                    }
                                            `}
                                            ></span>
                                            {item.status}
                                        </span>
                                    </td>

                                    {/* Timestamp */}
                                    <td className="py-4 px-4 whitespace-nowrap">
                                        <Typography className="text-sm text-gray-500">
                                            {formatDate(
                                                item.approved_timestamp
                                            )}
                                        </Typography>
                                    </td>

                                    <td className="py-4 px-4 text-left">
                                        <div className="flex flex-wrap gap-4">
                                            {item?.quotation_file && (
                                                <div className="flex flex-col items-start gap-1 group">
                                                    <DownloadFile
                                                        fileUrl={
                                                            item.quotation_file
                                                        }
                                                        className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                                                        label={t(
                                                            "quotation_label"
                                                        )}
                                                    />
                                                </div>
                                            )}

                                            {item.status !== "rejected" &&
                                                item?.purchase_order_file && (
                                                    <div className="flex flex-col items-start gap-1 group">
                                                        <DownloadFile
                                                            fileUrl={
                                                                item.purchase_order_file
                                                            }
                                                            className="flex items-center gap-1.5 text-emerald-600 hover:text-emerald-800 font-medium text-sm transition-colors"
                                                            label={t(
                                                                "purchase_order_label"
                                                            )}
                                                        />
                                                    </div>
                                                )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* ========================================================= */}
            {/* CUSTOMER PURCHASE ORDER LOGS */}
            {/* ========================================================= */}

            {purchase_order_logs?.length > 0 && (
                <div className="mt-8 w-full">
                    <div className="mb-4">
                        <Typography
                            variant="h6"
                            color="blue-gray"
                            className="font-bold"
                        >
                            {t("purchase_order_logs_title", {
                                count: purchase_order_logs?.length || 0,
                            })}
                        </Typography>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        {t("po_number_header")}
                                    </th>
                                    <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        {t("status_header")}
                                    </th>
                                    <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        {t("date_header")}
                                    </th>
                                    <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        {t("action_header")}
                                    </th>
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-200">
                                {purchase_order_logs.map((po, index) => (
                                    <tr
                                        key={po.id}
                                        className="hover:bg-gray-50 transition-colors"
                                    >
                                        <td className="py-4 px-4">
                                            <Typography className="text-sm font-medium text-gray-900">
                                                {po.customer_po_number || "-"}
                                            </Typography>
                                        </td>

                                        <td className="py-4 px-4">
                                            <span
                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                                ${po.status === "pending"
                                                        ? "bg-yellow-100 text-yellow-800"
                                                        : ""
                                                    }
                                                ${po.status === "approved"
                                                        ? "bg-green-100 text-green-800"
                                                        : ""
                                                    }
                                                ${po.status === "rejected"
                                                        ? "bg-red-100 text-red-800"
                                                        : ""
                                                    }
                                            `}
                                            >
                                                <span
                                                    className={`mr-1.5 h-1.5 w-1.5 rounded-full
                                                    ${po.status === "pending"
                                                            ? "bg-yellow-400"
                                                            : ""
                                                        }
                                                    ${po.status === "approved"
                                                            ? "bg-green-400"
                                                            : ""
                                                        }
                                                    ${po.status === "rejected"
                                                            ? "bg-red-400"
                                                            : ""
                                                        }
                                                `}
                                                ></span>
                                                {po.status}
                                            </span>
                                        </td>

                                        <td className="py-4 px-4 whitespace-nowrap">
                                            <Typography className="text-sm text-gray-500">
                                                {formatDate(po.created_at)}
                                            </Typography>
                                        </td>

                                        <td className="py-4 px-4 text-left">
                                            <div className="flex flex-col items-start gap-1.5">
                                                {po.customer_po_file && (
                                                    <DownloadFile
                                                        fileUrl={
                                                            po.customer_po_file
                                                        }
                                                        className="flex items-center gap-1.5 text-emerald-600 hover:text-emerald-800 font-medium text-sm transition-colors"
                                                        label={t(
                                                            "purchase_order_label"
                                                        )}
                                                    />
                                                )}

                                                {purchase_order_logs.length >
                                                    1 &&
                                                    index <
                                                    purchase_order_logs.length -
                                                    1 && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                                                            <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 rtl:ml-1.5 rtl:mr-0"></span>
                                                            {t(
                                                                "revised_po_badge"
                                                            )}
                                                        </span>
                                                    )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default OrderRfq;